// ---------------------------------------------------------------------------
// OfflineRouteDetailsPanel — surfaces context for routes restored from the
// offline cache so the user knows exactly what they're looking at (and that
// it isn't a fresh result). Includes a one-tap path back to live routing
// once the device is back online.
// ---------------------------------------------------------------------------

import { useRef } from "react";
import { Bike, Car, CloudOff, Download, Footprints, RefreshCw, Trash2, Upload, Wifi, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  clearOfflineTrip,
  downloadOfflineTrip,
  offlineTripAgeLabel,
  parseOfflineTripFile,
  saveOfflineTrip,
  type OfflineTripSnapshot,
} from "@/utils/offlineCache";

interface Props {
  snapshot: OfflineTripSnapshot;
  isOnline: boolean;
  routeCount: number;
  /** Called when the user wants to leave cached view and resume live routing. */
  onSwitchToLive: () => void;
  /** Hide the panel without clearing the cached routes. */
  onDismiss: () => void;
  /** Called after the saved snapshot is purged from localStorage. */
  onCleared?: () => void;
  /** Called after a snapshot is imported from a JSON file. */
  onImported?: (snap: OfflineTripSnapshot) => void;
}

const MODE_META: Record<string, { label: string; Icon: typeof Car }> = {
  walking: { label: "Walking", Icon: Footprints },
  biking: { label: "Biking", Icon: Bike },
  driving: { label: "Driving", Icon: Car },
};

export const OfflineRouteDetailsPanel = ({
  snapshot,
  isOnline,
  routeCount,
  onSwitchToLive,
  onDismiss,
  onCleared,
  onImported,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleClear = () => {
    clearOfflineTrip();
    toast.success("Saved offline trip cleared", {
      description: "Old cached routes were removed from this device.",
      duration: 3500,
    });
    onCleared?.();
  };
  const handleExport = () => {
    try {
      downloadOfflineTrip(snapshot);
      toast.success("Trip exported", {
        description: "Saved as a JSON file you can re-import later.",
        duration: 3000,
      });
    } catch {
      toast.error("Couldn't export trip", {
        description: "Your browser blocked the download. Try again.",
      });
    }
  };
  const handleImportClick = () => fileInputRef.current?.click();
  const handleImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-importing the same file works
    if (!file) return;
    try {
      const snap = await parseOfflineTripFile(file);
      saveOfflineTrip(snap);
      onImported?.(snap);
      toast.success("Trip imported", {
        description: `Restored ${snap.scored.length} ${snap.scored.length === 1 ? "route" : "routes"} from file.`,
        duration: 3500,
      });
    } catch (err) {
      toast.error("Couldn't import that file", {
        description: err instanceof Error ? err.message : "Unknown error.",
      });
    }
  };
  const meta = MODE_META[snapshot.mode ?? "walking"] ?? MODE_META.walking;
  const ModeIcon = meta.Icon;
  const startLabel = snapshot.startLabel || "Your starting point";
  const destLabel = snapshot.destLabel || "Saved destination";

  return (
    <div
      role="region"
      aria-label="Offline route details"
      className="fixed left-1/2 -translate-x-1/2 z-[55] w-[min(92vw,420px)] pointer-events-auto"
      style={{ top: "calc(env(safe-area-inset-top) + 3rem)" }}
    >
      <div className="rounded-2xl border border-amber-300/70 dark:border-amber-700/60 bg-amber-50/95 dark:bg-amber-950/85 backdrop-blur shadow-soft text-amber-950 dark:text-amber-50 overflow-hidden">
        <div className="flex items-start gap-2 px-3.5 py-2.5 border-b border-amber-300/50 dark:border-amber-700/40">
          <CloudOff className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide">
              Offline · cached route
            </div>
            <div className="text-[11px] opacity-80">
              {offlineTripAgeLabel(snapshot)} · {routeCount} {routeCount === 1 ? "option" : "options"} restored
            </div>
          </div>
          <button
            type="button"
            aria-label="Hide cached route panel"
            onClick={onDismiss}
            className="p-1 -m-1 rounded-md hover:bg-amber-200/60 dark:hover:bg-amber-800/60"
          >
            <X className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </div>

        <div className="px-3.5 py-3 space-y-2">
          <div className="flex items-start gap-2 text-sm">
            <span className="mt-1.5 w-2 h-2 rounded-full bg-emerald-500 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wide opacity-70">Start</div>
              <div className="truncate font-medium">{startLabel}</div>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <span className="mt-1.5 w-2 h-2 rounded-full bg-rose-500 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wide opacity-70">Destination</div>
              <div className="truncate font-medium">{destLabel}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs pt-1 opacity-90">
            <ModeIcon className="w-3.5 h-3.5" aria-hidden="true" />
            <span>{meta.label}</span>
          </div>
        </div>

        <div className="px-3.5 py-2.5 border-t border-amber-300/50 dark:border-amber-700/40 flex items-center gap-2">
          {isOnline ? (
            <Button
              size="sm"
              className="flex-1 h-8 text-xs"
              onClick={onSwitchToLive}
            >
              <Wifi className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
              Switch to live routes
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-8 text-xs"
              disabled
              title="You're offline — reconnect to plan a fresh trip"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
              Waiting for connection…
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-xs text-amber-900 dark:text-amber-100 hover:bg-amber-200/60 dark:hover:bg-amber-800/60"
            onClick={handleClear}
            aria-label="Clear saved offline trip"
            title="Remove cached routes from this device"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
            Clear
          </Button>
        </div>

        <div className="px-3.5 pb-2.5 -mt-1 flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-8 text-xs"
            onClick={handleExport}
            aria-label="Export saved trip as JSON"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
            Export
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-8 text-xs"
            onClick={handleImportClick}
            aria-label="Import a saved trip JSON file"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
            Import
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleImportChange}
          />
        </div>
      </div>
    </div>
  );
};
